import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Zap, ClipboardPaste } from "lucide-react";
import { extractSubdivision, extractTownship, isRecordBlacklisted, getBlacklistedSubdivisions, getBlacklistedTownships } from "@/utils/subdivisionBlacklist";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";
import { SubdivisionBlacklistManager } from "./SubdivisionBlacklistManager";

interface DallasDocument {
  documentNumber: string;
  filingDate: string;
  grantor: string;
  grantee: string;
  docType: string;
  releaseDate: string | null;
  legalDescription: string;
}

interface Props {
  onRecordsSaved: () => void;
}

const CORPORATE_KEYWORDS = [
  'BANK', 'MORTGAGE', 'LENDING', 'CREDIT UNION', 'FINANCE', 'FUNDING',
  'LLC', 'LTD', 'INC', 'CORP', 'CORPORATION', 'FEDERAL', 'NATIONAL',
  'SERVICES', 'SOLUTIONS', 'CAPITAL', 'INVESTMENTS', 'HOLDINGS',
  'WELLS FARGO', 'CHASE', 'PENNYMAC', 'DHI', 'USAA', 'NAVY FEDERAL',
  'VETERANS UNITED', 'GUILD', 'CHURCHILL', 'FAIRWAY', 'NEWREZ',
  'BENCHMARK', 'SWBC', 'CANOPY', 'FLAGSTAR', 'FIDELITY',
  'FIRST NATIONAL', 'FIRST FINANCIAL', 'FIRST CONTINENTAL',
  'VANDERBI', 'PLANET HOME', 'VILLAGE CAPITAL', 'ANGEL OAK',
  'UNITED WHOLESALE', 'SENTE', 'LOAN STORE',
  'HOUSING & URBAN', 'HOUSING&URBAN', 'HUD', 'SECRETARY OF HOUSING',
  'MORTGAGE RESEARCH CENTER', 'MORTGAGE SOLUTIONS',
  'READYCAP', 'ROCKET MORTGAGE', 'PENNYMAC LOAN', 'PILGRIM MORTGAGE',
  'CALIBER HOME', 'QUICKEN LOANS', 'LOANDEPOT', 'MR COOPER',
  'PRIMELENDING', 'PROVIDENT FUNDING', 'CRESTMARK', 'ASPIRE FINANCIAL',
  'GUARANTEED RATE', 'HOME POINT', 'CARDINAL FINANCIAL', 'NATIONS LENDING',
  'EUSTIS MORTGAGE', 'SISTAR MORTGAGE', 'REPUBLIC STATE', 'ABSOLUTE MORTGAGE',
  'GATEWAY MORTGAGE', 'JPMORGAN', 'BANCORPSOUTH', 'THRIVE MORTGAGE',
  'SERVICE FIRST', 'CORNERSTONE HOME', 'COLONIAL NATIONAL', 'WATERSTONE MORTGAGE',
  'HIGHLANDS RESIDENTIAL', 'MOVEMENT MORTGAGE', 'CROSSCOUNTRY MORTGAGE',
  'FIRST HORIZON', 'ACADEMY MORTGAGE', 'PROSPERITY HOME', 'PULTE MORTGAGE',
  'FREEDOM MORTGAGE', 'AMERISAVE', 'BETTER MORTGAGE', 'WILLOW BEND',
  'WALLICK & VOLK', 'CERTIFIED FUNDING', 'WYNDHAM CAPITAL', 'EVERETT FINANCIAL',
  'GREAT WESTERN', 'DAS ACQUISITION', 'DALLAS MORTGAGE', 'FIRST RATE FINANCIAL',
  'AMERIDREAM', 'GUARDIAN MORTGAGE', 'AMWEST FUNDING', 'ARK LA TEX',
  'ARK-LA-TEX', 'HOMEWOOD MORTGAGE', 'MOMENTUM LOANS', 'SWBC MORTGAGE',
  'NORTH DALLAS', 'TEXAS REPUBLIC', 'GOLDWATER BANK', 'SIMMONS BANK',
  'NTFN', 'FIRSTBANK', 'SILVERTON MORTGAGE', 'HOMEWAY MORTGAGE',
  'LAKEVIEW LOAN', 'TOWN SQUARE', 'MCI MORTGAGE', 'TRINITY OAKS',
  'COMMERCE HOME', 'COMMUNITY RESOURCE', 'GREATWALL', 'GEO CORP',
  'GRIFFIN FUNDING', 'GRAND HOME LOANS', 'AMRES CORPORATION',
  'AMSW LENDING', 'CITIZENS BANK', 'TRUIST BANK', 'TIGER LOANS',
  'TEXAS STATE AFFORDABLE', 'TEXAS DEPARTMENT', 'TEXAS BANK FINANCIAL',
  'KELLER MORTGAGE', 'CITY BANK', 'STATE FARM', 'ANGEL OAK MORTGAGE',
  'AMERICAN FINANCIAL NETWORK', 'AMERICAN HERITAGE CAPITAL',
  'TEXAS MORTGAGE PROFESSIONALS', 'MCAP MORTGAGE', 'RANDOLPH BROOKS',
  'UNIVERSITY FEDERAL', 'BANK OF AMERICA', 'US BANK', 'U S BANK',
  'K HOVNANIAN', 'FINANCE AMERICA', 'HOMESMART FINANCIAL',
  'HOMETRUST MORTGAGE', 'BROOKHOLLOW MORTGAGE', 'PRIMARY RESIDENTIAL',
  'VILLAGE CAPITAL & INVESTMENT', 'SELECT PORTFOLIO', 'UIF CORPORATION',
  'SAISON FUNDEX', 'SWC RHP', 'TEXAN MORTGAGE',
  'UNITED STATES OF AMERICA', 'CABB FINANCE',
];

export function DallasCountyTextParser({ onRecordsSaved }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const [scrapeStartDate, setScrapeStartDate] = useState("");
  const [scrapeEndDate, setScrapeEndDate] = useState("");
  const [scrapeResult, setScrapeResult] = useState<any>(null);
  const { toast } = useToast();

  const parseDallasData = (text: string): DallasDocument[] => {
    const lines = text.trim().split('\n');
    const documents: DallasDocument[] = [];

    for (const line of lines) {
      let trimmed = line.trim();
      if (!trimmed) continue;
      trimmed = trimmed.replace(/^menu\s*icon\s*/i, '').trim();
      if (!trimmed) continue;

      const parts = trimmed.split('\t').map(p => p.trim());
      if (parts.length < 6) continue;

      const grantor = parts[0];
      const grantee = parts[1];
      const docType = parts[2];
      const rawDate = parts[3];
      const documentNumber = parts[4];
      const releaseRaw = parts[5];
      const legalDescription = parts[7] || '';

      if (!docType.toUpperCase().includes('DEED OF TRUST')) continue;

      const dateParts = rawDate.split('/');
      const filingDate = dateParts.length === 3
        ? `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`
        : new Date().toISOString().split('T')[0];

      const releaseDate = releaseRaw && !releaseRaw.includes('--') ? releaseRaw : null;

      documents.push({
        documentNumber,
        filingDate,
        grantor,
        grantee,
        docType: 'DEED OF TRUST',
        releaseDate,
        legalDescription,
      });
    }

    return documents;
  };

  const isCorpLender = (name: string): boolean => {
    const normalizedName = name
      .toUpperCase()
      .replace(/[.,]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return CORPORATE_KEYWORDS.some((rawKeyword) => {
      const keyword = rawKeyword
        .toUpperCase()
        .replace(/[.,]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (!keyword) return false;

      const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
      return new RegExp(`\\b${escaped}\\b`, "i").test(normalizedName);
    });
  };

  const handleProcess = async () => {
    if (!textData.trim()) {
      toast({ title: "No data", description: "Please paste records", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    try {
      const documents = parseDallasData(textData);
      if (documents.length === 0) {
        toast({ title: "No records found", description: "Could not parse any DEED OF TRUST records from the pasted text.", variant: "destructive" });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      const potentialOwnerFinanced = documents.filter(doc => !isCorpLender(doc.grantee));
      const corporateCount = documents.length - potentialOwnerFinanced.length;
      console.log(`Dallas: Parsed ${documents.length} docs, ${corporateCount} corporate, ${potentialOwnerFinanced.length} potential owner-financed`);

      if (potentialOwnerFinanced.length === 0) {
        toast({
          title: "No owner-financed deals found",
          description: `Analyzed ${documents.length} records — all had corporate/institutional lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      const deedText = potentialOwnerFinanced.map(d =>
        `Document: ${d.documentNumber}\n` +
        `Date: ${d.filingDate}\n` +
        `Grantor (Borrower): ${d.grantor}\n` +
        `Grantee (Lender/Beneficiary): ${d.grantee}\n` +
        `Legal: ${d.legalDescription}\n`
      ).join('\n---\n');

      const { data: filterData, error: filterError } = await supabase.functions.invoke(
        'filter-owner-finance',
        { body: { deedText } }
      );

      if (filterError) throw filterError;

      const ownerFinancedDeals = filterData?.filteredDeals || [];
      const ownerFinancedDocNumbers = new Set(
        ownerFinancedDeals.map((deal: any) => deal.documentNumber)
      );

      const confirmedRecords = potentialOwnerFinanced.filter(d =>
        ownerFinancedDocNumbers.has(d.documentNumber)
      );

      console.log(`AI confirmed ${confirmedRecords.length} owner-financed deals`);

      if (confirmedRecords.length === 0) {
        toast({
          title: "No owner-financed deals confirmed",
          description: `Hard filter passed ${potentialOwnerFinanced.length}, but AI rejected all as non-individual lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      const existingQuery = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'dallas')
        .in('document_number', confirmedRecords.map(r => r.documentNumber));

      const existingDocs = new Set(existingQuery.data?.map(d => d.document_number) || []);
      const newRecords = confirmedRecords.filter(r => !existingDocs.has(r.documentNumber));

      if (newRecords.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${confirmedRecords.length} owner-financed deals, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Split by township + subdivision blacklist
      const cleanRecords: typeof newRecords = [];
      const blacklistedRecords: { record: typeof newRecords[0]; reason: string }[] = [];
      
      for (const record of newRecords) {
        const result = isRecordBlacklisted(record.legalDescription);
        if (result.blacklisted) {
          blacklistedRecords.push({ record, reason: result.reason! });
        } else {
          cleanRecords.push(record);
        }
      }

      const allToInsert = [
        ...cleanRecords.map(record => ({
          county: 'dallas',
          document_number: record.documentNumber,
          filing_date: record.filingDate,
          document_type: record.docType,
          grantor_name: record.grantor,
          grantee_name: record.grantee,
          legal_description: record.legalDescription,
          is_potential_owner_finance: true,
          confidence_score: 80,
          detection_reasons: ['AI confirmed individual beneficiary/lender'],
          status: 'new',
          raw_data: { grantor: record.grantor, grantee: record.grantee, subdivision: extractSubdivision(record.legalDescription), township: extractTownship(record.legalDescription) } as any,
        })),
        ...blacklistedRecords.map(({ record, reason }) => ({
          county: 'dallas',
          document_number: record.documentNumber,
          filing_date: record.filingDate,
          document_type: record.docType,
          grantor_name: record.grantor,
          grantee_name: record.grantee,
          legal_description: record.legalDescription,
          is_potential_owner_finance: true,
          confidence_score: 80,
          detection_reasons: ['AI confirmed individual beneficiary/lender', reason],
          status: 'blacklisted',
          raw_data: { grantor: record.grantor, grantee: record.grantee, subdivision: extractSubdivision(record.legalDescription), township: extractTownship(record.legalDescription) } as any,
        })),
      ];

      if (allToInsert.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${confirmedRecords.length} owner-financed deals, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      const { error: insertError } = await supabase
        .from('county_research')
        .upsert(allToInsert, { onConflict: 'document_number', ignoreDuplicates: false });

      if (insertError) throw insertError;

      backupRecordsToExcel(allToInsert, 'Dallas', 'dallas_parser');

      const blMsg = blacklistedRecords.length > 0 ? ` | Blacklisted: ${blacklistedRecords.length}` : '';
      toast({
        title: "Success!",
        description: `Saved ${cleanRecords.length} owner-financed deals!${blMsg}\n` +
          `Total: ${documents.length} | Corporate: ${corporateCount} | ` +
          `AI confirmed: ${confirmedRecords.length} | New saved: ${cleanRecords.length}`,
      });

      setTextData("");
      onRecordsSaved();
    } catch (error) {
      console.error('Error processing Dallas County data:', error);
      toast({ title: "Error", description: "Failed to process records. Please try again.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBulkScrape = async () => {
    if (!scrapeStartDate || !scrapeEndDate) {
      toast({ title: "Date range required", description: "Please enter start and end dates", variant: "destructive" });
      return;
    }

    setIsScraping(true);
    setScrapeResult(null);

    try {
      const formatDate = (d: string) => {
        const [y, m, day] = d.split('-');
        return `${m}/${day}/${y}`;
      };

      let currentPage = 1;
      const maxPages = 8; // Safety limit
      let totalSaved = 0;
      let totalScraped = 0;
      let totalCorporateFiltered = 0;
      let totalAiConfirmed = 0;
      let lastPaginationInfo = "";
      let hasMorePages = true;

      while (hasMorePages && currentPage <= maxPages) {
        console.log(`Scraping page ${currentPage}...`);

        const { data, error } = await supabase.functions.invoke('scrape-dallas-county', {
          body: {
            startDate: formatDate(scrapeStartDate),
            endDate: formatDate(scrapeEndDate),
            page: currentPage,
            blacklistedSubdivisions: getBlacklistedSubdivisions(),
            blacklistedTownships: getBlacklistedTownships(),
          }
        });

        if (error) throw error;

        if (!data.success) {
          if (currentPage === 1) {
            toast({
              title: "Scrape Failed",
              description: data.error || "Unknown error occurred",
              variant: "destructive",
            });
            setScrapeResult(data);
            return;
          }
          // If later pages fail, just stop
          break;
        }

        totalScraped += data.totalScraped || 0;
        totalCorporateFiltered += data.corporateFiltered || 0;
        totalAiConfirmed += data.aiConfirmed || 0;
        totalSaved += (data.newSaved || 0) + (data.restored || 0);
        lastPaginationInfo = data.paginationInfo || lastPaginationInfo;
        hasMorePages = data.hasMorePages === true;

        // Update UI with running totals
        setScrapeResult({
          success: true,
          totalScraped,
          corporateFiltered: totalCorporateFiltered,
          aiConfirmed: totalAiConfirmed,
          newSaved: totalSaved,
          paginationInfo: lastPaginationInfo,
          currentPage,
          hasMorePages,
          message: `Page ${currentPage} complete. ${hasMorePages ? 'Continuing...' : 'Done!'}`,
        });

        if (data.newSaved > 0 || data.restored > 0) {
          onRecordsSaved();
        }

        currentPage++;
      }

      toast({
        title: "Scrape Complete!",
        description: `${currentPage - 1} pages scraped. ${totalSaved} owner-financed deals saved.`,
      });

      setScrapeResult({
        success: true,
        totalScraped,
        corporateFiltered: totalCorporateFiltered,
        aiConfirmed: totalAiConfirmed,
        newSaved: totalSaved,
        paginationInfo: lastPaginationInfo,
        currentPage: currentPage - 1,
        hasMorePages: false,
        message: `Complete: ${currentPage - 1} pages, ${totalSaved} deals saved`,
      });
    } catch (error: any) {
      console.error('Error scraping Dallas County:', error);
      toast({
        title: "Scrape Error",
        description: error.message || "Failed to scrape Dallas County. Check edge function logs.",
        variant: "destructive",
      });
    } finally {
      setIsScraping(false);
    }
  };

  const previewCount = textData.trim() ? parseDallasData(textData).length : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dallas County, TX - Deed of Trust Research</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="bulk-scrape">
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="bulk-scrape" className="gap-2">
                <Zap className="h-4 w-4" />
                Bulk Scrape
              </TabsTrigger>
              <TabsTrigger value="manual-paste" className="gap-2">
                <ClipboardPaste className="h-4 w-4" />
                Manual Paste
              </TabsTrigger>
            </TabsList>
            <Button
              size="sm"
              onClick={handleProcess}
              disabled={isProcessing || !textData.trim()}
            >
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Process {previewCount > 0 ? `(${previewCount})` : ''}
            </Button>
          </div>

          <TabsContent value="bulk-scrape" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Automatically scrape Dallas County PublicSearch for Deed of Trust records.
              Uses Firecrawl + AI to extract, filter corporate lenders, and save owner-financed deals.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Start Date</label>
                <Input
                  type="date"
                  value={scrapeStartDate}
                  onChange={(e) => setScrapeStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">End Date</label>
                <Input
                  type="date"
                  value={scrapeEndDate}
                  onChange={(e) => setScrapeEndDate(e.target.value)}
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              💡 Keep date ranges to 1-2 weeks for best results. Larger ranges may have many pages of results.
            </p>

            <Button
              onClick={handleBulkScrape}
              disabled={isScraping || !scrapeStartDate || !scrapeEndDate}
              className="w-full"
            >
              {isScraping ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Scraping → Filtering → AI Verifying → Saving...
                </>
              ) : (
                "Scrape Dallas County Records"
              )}
            </Button>

            {isScraping && (
              <div className="rounded-lg border bg-muted/50 p-4 text-center space-y-2">
                <p className="text-sm font-medium">
                  {scrapeResult?.currentPage ? `Processing page ${scrapeResult.currentPage}...` : 'Starting scrape...'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Auto-paginating through all results pages. Each page: Capture → Extract → Filter → Verify → Save
                </p>
                {scrapeResult?.success && (
                  <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                    <div className="rounded bg-background p-1">Scraped: {scrapeResult.totalScraped}</div>
                    <div className="rounded bg-background p-1">Saved: {scrapeResult.newSaved}</div>
                  </div>
                )}
              </div>
            )}

            {!isScraping && scrapeResult && (
              <div className="rounded-lg border p-4 space-y-2">
                <p className="text-sm font-medium">
                  {scrapeResult.success ? '✅ Scrape Results' : '❌ Scrape Failed'}
                </p>
                {scrapeResult.success ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="rounded bg-muted p-2 text-center">
                        <p className="text-lg font-bold">{scrapeResult.totalScraped}</p>
                        <p className="text-xs text-muted-foreground">Total Scraped</p>
                      </div>
                      <div className="rounded bg-muted p-2 text-center">
                        <p className="text-lg font-bold">{scrapeResult.corporateFiltered}</p>
                        <p className="text-xs text-muted-foreground">Corporate Lenders Removed</p>
                      </div>
                      <div className="rounded bg-muted p-2 text-center">
                        <p className="text-lg font-bold">{scrapeResult.aiConfirmed}</p>
                        <p className="text-xs text-muted-foreground">AI Confirmed</p>
                      </div>
                      <div className="rounded bg-muted p-2 text-center">
                        <p className="text-lg font-bold text-primary">{scrapeResult.newSaved}</p>
                        <p className="text-xs text-muted-foreground">New Saved</p>
                      </div>
                    </div>
                    {scrapeResult.currentPage && (
                      <p className="text-xs text-muted-foreground text-center">
                        📄 Pages processed: {scrapeResult.currentPage}
                        {scrapeResult.paginationInfo && ` | ${scrapeResult.paginationInfo}`}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-destructive">{scrapeResult.error}</p>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="manual-paste" className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Paste deed of trust records from the Dallas County Public Records website.
                Copy rows directly from the search results (tab-separated format).
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Format:</strong> GRANTOR {'\t'} GRANTEE {'\t'} DEED OF TRUST {'\t'} DATE {'\t'} DOC# {'\t'} RELEASE_DATE {'\t'} CITY {'\t'} LEGAL_DESCRIPTION
              </p>
              <p className="text-sm text-muted-foreground">
                Corporate/institutional lenders are automatically filtered out. Remaining records are verified by AI.
              </p>
            </div>

            <Textarea
              value={textData}
              onChange={(e) => setTextData(e.target.value)}
              placeholder={"menu icon\tGARCIA GABRIELA\tSECRETARY OF HOUSING&URBAN DEVELOPMENT\tDEED OF TRUST\t1/5/2024\t202400003244\t--/--/--\tDALLAS\tSubdivision- Town: DALLAS Name: BEVERLY HILLS Lot: 10 Block: 7"}
              className="min-h-[300px] font-mono text-sm"
            />

            {previewCount > 0 && (
              <p className="text-sm text-muted-foreground">
                Detected <strong>{previewCount}</strong> DEED OF TRUST records to analyze
              </p>
            )}

            <Button
              onClick={handleProcess}
              disabled={isProcessing || !textData.trim()}
              className="w-full"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Filtering corporate lenders + AI verification...
                </>
              ) : (
                `Process ${previewCount > 0 ? previewCount + ' ' : ''}Records`
              )}
            </Button>

            {isProcessing && (
              <p className="text-sm text-muted-foreground text-center">
                Hard-filtering corporate lenders, then AI verifying remaining records...
              </p>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      <div className="px-6 pb-6 pt-2">
        <SubdivisionBlacklistManager />
      </div>
    </Card>
  );
}
